import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { supabase } from '../lib/supabase.ts';
import LoginModal from './LoginModal.tsx';
import { useIsMobile } from '../hooks/useMediaQuery.ts';
import styles from './Header.module.css';

const NoticeBanner: React.FC = () => {
  const history = useHistory();
  const [currentNotice, setCurrentNotice] = useState(0);
  const [notices, setNotices] = useState<{ id: string; title: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchNotices = async () => {
      try {
        if (abortController.signal.aborted) return;

        const { data, error } = await supabase
          .from('notices')
          .select('id, title')
          .eq('is_banner', true)
          .order('created_at', { ascending: false })
          .limit(5)
          .abortSignal(abortController.signal);

        if (error) throw error;

        if (!abortController.signal.aborted) {
          setNotices(data || []);
          setLoading(false);
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('Error fetching notices:', error);
          setLoading(false);
        }
      }
    };

    fetchNotices();
    const interval = setInterval(() => {
      if (!abortController.signal.aborted) {
        fetchNotices();
      }
    }, 60000);

    return () => {
      abortController.abort();
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentNotice(prev => (prev + 1) % notices.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [notices.length]);

  const nextNotice = () => {
    setCurrentNotice(prev => (prev + 1) % notices.length);
  };

  const prevNotice = () => {
    setCurrentNotice(prev => (prev - 1 + notices.length) % notices.length);
  };

  return (
    <div className={styles.noticeBanner}>
      <button onClick={prevNotice} className={`${styles.noticeButton} ${styles.prev}`}>
        ‹
      </button>
      <div className={styles.noticeContent}>
        {loading ? (
          <span> </span>
        ) : notices.length > 0 ? (
          <span onClick={() => history.push(`/notices/${notices[currentNotice].id}`)}>
            📢 {notices[currentNotice].title}
          </span>
        ) : (
          <span onClick={() => history.push('/notices')}>
            🎉 Tokyo Book Tree에 오신 것을 환영합니다!
          </span>
        )}
      </div>
      <button onClick={nextNotice} className={`${styles.noticeButton} ${styles.next}`}>
        ›
      </button>
    </div>
  );
};

interface MobileApplyButton {
  show: boolean;
  text: string;
  price: string;
  onClick: () => void;
  buttonText?: string;
  buttonColor?: string;
}

interface HeaderProps {
  currentPage: number;
  totalPages: number;
  pageTitle: string;
  onPageChange: (index: number) => void;
  onProfileClick: () => void;
  isLoggedIn?: boolean;
  mobileApplyButton?: MobileApplyButton;
}

const Header: React.FC<HeaderProps> = ({
  currentPage,
  totalPages,
  pageTitle,
  onPageChange,
  onProfileClick,
  isLoggedIn: propIsLoggedIn,
  mobileApplyButton,
}) => {
  const history = useHistory();
  const location = useLocation();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const isLoggedIn = !!user;
  const pages = ['MARKET', 'CLUB', 'EVENTS'];
  const [unreadCount, setUnreadCount] = useState(0);
  const [unwrittenPreparations, setUnwrittenPreparations] = useState(0);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showWriteMenu, setShowWriteMenu] = useState(false);

  // 전역 함수로 읽지 않은 메시지 수 업데이트
  useEffect(() => {
    (window as any).updateUnreadCount = (count: number) => {
      setUnreadCount(count);
    };

    return () => {
      delete (window as any).updateUnreadCount;
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      setUnwrittenPreparations(0);
      return;
    }

    const abortController = new AbortController();

    const checkUnreadMessages = async () => {
      try {
        const { count, error } = await supabase
          .from('message_recipients')
          .select('*', { count: 'exact', head: true })
          .eq('recipient_id', user.id)
          .eq('is_read', false)
          .abortSignal(abortController.signal);

        if (!error && !abortController.signal.aborted) {
          setUnreadCount(count || 0);
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('Error checking unread messages:', error);
        }
      }
    };

    const checkUnwrittenPreparations = async () => {
      try {
        const { data: participantData, error: participantError } = await supabase
          .from('lab_schedule_participants')
          .select('schedule_id')
          .eq('user_id', user.id)
          .eq('status', 'confirmed')
          .abortSignal(abortController.signal);

        if (participantError || !participantData) return;

        const scheduleIds = participantData.map(p => p.schedule_id);
        if (scheduleIds.length === 0) return;

        const { data: upcomingSchedules, error: scheduleError } = await supabase
          .from('lab_schedules')
          .select('id, preparation_topic, date')
          .in('id', scheduleIds)
          .not('preparation_topic', 'is', null)
          .gte('date', new Date().toISOString().split('T')[0])
          .abortSignal(abortController.signal);

        if (scheduleError || !upcomingSchedules) return;

        const { data: preparationsData, error: prepError } = await supabase
          .from('lab_schedule_preparations')
          .select('schedule_id')
          .eq('user_id', user.id)
          .in(
            'schedule_id',
            upcomingSchedules.map(s => s.id)
          )
          .abortSignal(abortController.signal);

        if (prepError) return;

        const writtenScheduleIds = (preparationsData || []).map(prep => prep.schedule_id);
        const unwrittenCount = upcomingSchedules.filter(
          schedule => !writtenScheduleIds.includes(schedule.id)
        ).length;

        if (!abortController.signal.aborted) {
          setUnwrittenPreparations(unwrittenCount);
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('Error checking unwritten preparations:', error);
        }
      }
    };

    checkUnreadMessages();
    checkUnwrittenPreparations();

    const handleVisibilityChange = () => {
      if (!document.hidden && !abortController.signal.aborted) {
        setTimeout(() => {
          if (!abortController.signal.aborted) {
            checkUnreadMessages();
            checkUnwrittenPreparations();
          }
        }, 100);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      abortController.abort();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  return (
    <div className={styles.headerWrapper}>
      <header className={styles.header}>
        <div onClick={() => history.push('/')} className={styles.logo}>
          Tokyo Book Tree
        </div>

        {!isMobile && (
          <nav className={styles.nav}>
            {pages.map((page, index) => (
              <button
                key={index}
                onClick={() => {
                  if (index === 0) {
                    history.push('/marketplace');
                  } else if (index === 1) {
                    history.push('/clubs');
                  } else if (index === 2) {
                    history.push('/events');
                  }
                  const pageContainer = document.querySelector(
                    '[data-page-container]'
                  ) as HTMLElement;
                  if (pageContainer) {
                    pageContainer.scrollTop = 0;
                  }
                }}
                className={`${styles.navButton} ${(index === 0 && location.pathname === '/marketplace') || (index === 1 && location.pathname === '/clubs') || (index === 2 && location.pathname === '/events') ? styles.active : ''}`}
              >
                {page}
              </button>
            ))}
          </nav>
        )}



        {!isMobile && (
          <div className={styles.profileSection}>
            <div style={{ position: 'relative' }}>
              <button
                onClick={onProfileClick}
                title={isLoggedIn ? '프로필' : '구글 로그인'}
                className={styles.profileButton}
              >
                {isLoggedIn ? '●' : 'G'}
              </button>
              {isLoggedIn && (unreadCount > 0 || unwrittenPreparations > 0) && (
                <div className={styles.notificationBadge} />
              )}
            </div>
          </div>
        )}
      </header>

      {/* Notice Banner */}
      <NoticeBanner />

      {isMobile && mobileApplyButton?.show && (
        <div className={styles.mobileApplyBar}>
          <div className={styles.applyInfo}>
            <div className={styles.applyText}>{mobileApplyButton.text}</div>
            <div className={styles.applyPrice}>{mobileApplyButton.price}</div>
          </div>
          <button
            onClick={mobileApplyButton.onClick}
            className={styles.applyButton}
            style={{ background: mobileApplyButton.buttonColor || undefined }}
          >
            {mobileApplyButton.buttonText || '참가하기'}
          </button>
        </div>
      )}

      {isMobile && !location.pathname.includes('/club/') && (
        <div className={styles.mobileBottomNav}>
          <button
            onClick={() => history.push('/marketplace')}
            className={`${styles.mobileNavButton} ${location.pathname === '/marketplace' ? styles.active : ''}`}
          >
            <span>▤</span>
            <span>MARKET</span>
          </button>
          <button
            onClick={() => history.push('/clubs')}
            className={`${styles.mobileNavButton} ${location.pathname === '/clubs' ? styles.active : ''}`}
          >
            <span>◆</span>
            <span>CLUB</span>
          </button>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => {
                if (user) {
                  setShowWriteMenu(!showWriteMenu);
                } else {
                  setShowLoginModal(true);
                }
              }}
              className={styles.mobileNavButton}
            >
              <span>+</span>
              <span>ADD</span>
            </button>
            {showWriteMenu && (
              <>
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 998,
                  }}
                  onClick={() => setShowWriteMenu(false)}
                />
                <div
                  style={{
                    position: 'absolute',
                    bottom: '60px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    padding: '0.5rem',
                    minWidth: '120px',
                    zIndex: 999,
                    border: '1px solid #e8e6e1',
                  }}
                >
                  <button
                    onClick={() => {
                      setShowWriteMenu(false);
                      history.push('/marketplace/register');
                    }}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      color: '#2a2a2a',
                      textAlign: 'left',
                      fontWeight: '500',
                    }}
                  >
                    ▤ 도서 등록
                  </button>
                  <button
                    onClick={() => {
                      setShowWriteMenu(false);
                      history.push('/create-club');
                    }}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      color: '#2a2a2a',
                      textAlign: 'left',
                      fontWeight: '500',
                    }}
                  >
                    ◆ 클럽 생성
                  </button>
                </div>
              </>
            )}
          </div>
          <button
            onClick={() => history.push('/events')}
            className={`${styles.mobileNavButton} ${location.pathname === '/events' ? styles.active : ''}`}
          >
            <span>★</span>
            <span>EVENTS</span>
          </button>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => {
                if (user) {
                  history.push('/profile');
                } else {
                  setShowLoginModal(true);
                }
              }}
              className={styles.mobileNavButton}
            >
              <span>{user ? '●' : 'G'}</span>
              <span>{user ? 'MY PAGE' : 'LOGIN'}</span>
            </button>
            {user && (unreadCount > 0 || unwrittenPreparations > 0) && (
              <div className={styles.notificationBadge} />
            )}
          </div>
        </div>
      )}

      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
    </div>
  );
};

export default Header;
